import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { Input } from "antd"
import { Modal } from "components/ui/modals/Modal/Modal"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { SearchGlassIcon } from "components/ui/icons/input/SearchGlassIcon"
import { SegmentedWithoutBg } from "components/ui/segmented/SegmentedWithoutBg/SegmentedWithoutBg"
import { CustomModelNumberModal } from "components/pages/PageAllFiles/layout/FileInfo/layout/CustomModelNumbers/CustomModelNumberModal"
import { useDebounce } from "hooks/debounce.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import {
	CUSTOM_MODEL_NUMBERS_QUERY_KEY,
	useCreateCustomModelNumber,
	useGetCustomModelNumbers,
} from "hooks/api/customModelNumbers/customModelNumbers.hook"
import { useGetModelNumbersByIds, useSearchModelNumbers } from "hooks/api/modelNumbers/modelNumbers.hook"
import { useObjectsCategories } from "hooks/api/objectsCategories/objectsCategories.hook"
import { useGetSeries } from "hooks/api/series/series.hook"
import {
	computeMatch,
	computeMatchForMany,
	findAllModelNumbers,
	findModelNumbersForCategory,
	normalizeModelNumber,
} from "helpers/modelNumberMatch.helper"
import {
	MAX_VISIBLE_RESULTS,
	MIN_SEARCH_CHARS,
	flattenCatalogue,
	hasPriceForSeries,
	isCategoryChange as isCategoryChangeFor,
	matchQualityRank,
	relevanceRank,
	splitCategoryPath,
	toCatalogueModelNumber,
} from "helpers/modelNumberCatalogue.helper"
import {
	customModelNumberMatchesTerm,
	toSelectableCustomModelNumber,
} from "helpers/customModelNumber.helper"
import { ModelNumberRow } from "./ModelNumberRow"
import type { InputRef } from "antd"
import type { TDimensionGroup, TFurniture } from "types/elevationReferences.type"
import type { TCustomModelNumber, TCustomModelNumberPayload } from "types/customModelNumbers.type"
import type {
	TCatalogueModelNumber,
	TCategoriesWithModels,
	TSelectableModelNumber,
} from "types/modelNumbers.type"

import style from "./SelectModelNumberModal.module.scss"

const {
	body,
	section,
	sectionTitle,
	dimensionsRow,
	dimensionGroupGrid,
	dimensionCount,
	dimensionLabel,
	unset,
	warning,
	seriesValue,
	scopeRow,
	segmented,
	list,
	groupLabel,
	stateBlock,
	stateTitle,
	stateSubtitle,
	footer,
	footerNotice,
	footerNoticeStop,
	footerNoticeDot,
	footerNoticeStrong,
	footerButtons,
	footerButton,
	searchIcon,
	search,
	createButton,
} = style

/**
 * Everything the picker needs to know about the object it is picking FOR: the three category levels
 * (the leaf scopes the candidate list; all three render the current-category notice), the
 * ML-detected numbers that tag rows, the dimensions that score the match, and the current selection
 * that seeds the pinned row. Deliberately narrower than `TFurniture` so the create-object panel
 * (REQ-293) — which has a form, not a saved furniture — can reuse the picker unchanged.
 */
export type TModelNumberTarget = Pick<
	TFurniture,
	| "category"
	| "super_category"
	| "top_level_category"
	| "detected_model_numbers"
	| "selected_model_number"
	| "width"
	| "height"
	| "depth"
>

/** Which pool of model numbers the list is drawn from. */
type TScope = "category" | "all" | "custom"

const CATEGORY_SCOPE: TScope = "category"
const ALL_SCOPE: TScope = "all"
/** #1346 — the drawing's own Custom Model Number definitions. */
const CUSTOM_SCOPE: TScope = "custom"

/** ms of quiet typing before the catalogue-wide search hits the backend. */
const SEARCH_DEBOUNCE_MS = 300

type Props = {
	isOpen: boolean
	onClose: () => void
	furniture: TModelNumberTarget
	seriesOverrideId: number | null
	initialSelectedId: number | null
	/**
	 * `useArbitraryEndpoint` is `true` when the pick came from the catalogue-wide
	 * scope, where the model number may belong to another category. The parent then
	 * has to save through `select-arbitrary-model-number/`, which applies the model
	 * number AND the category move in one transaction — the plain furniture PATCH
	 * rejects a cross-category id.
	 */
	onSave: (
		model: TSelectableModelNumber,
		options: {
			/**
			 * The Custom Model Number the pick came from (#1346), or `null` for a Stevens one. Non-null
			 * means the save has to go through `custom-model-number/`, which ALSO converts the object —
			 * so this is what turns a cabinet into a custom object in one action, and the other two
			 * options below are then irrelevant.
			 */
			customModelNumber: TCustomModelNumber | null
			useArbitraryEndpoint: boolean
			/**
			 * The `/categories/` node the pick actually belongs to, when it is known — i.e. for a
			 * category-scope row. A caller that OWNS the category rather than moving it (the REQ-293
			 * create panel, which is still building its POST body) sends this as its
			 * `related_category_id`, so a peer-variant pick can't produce a mismatched pair. Null for
			 * a catalogue-wide row, whose rows carry names rather than ids.
			 */
			categoryNodeId: number | null
		},
	) => Promise<void>
	isOther?: boolean
	/**
	 * The target is ALREADY a custom object (#1346). Opens the picker on the Custom tab and turns its
	 * radio on for `initialCustomModelNumberId`, so reopening the picker shows what is linked rather
	 * than an empty Object-category tab (a custom object has no category to browse).
	 */
	isCustom?: boolean
	/** The linked Custom Model Number's id, so its row opens selected. */
	initialCustomModelNumberId?: number | null
	/**
	 * REQ-403 batch mode: the DISTINCT width/height/depth triples of the whole selection, each with
	 * its multiplicity. When present it replaces the single object's W/H/D line in the "Cabinet
	 * dimensions" block AND becomes the match target set — a candidate's axis counts as in range only
	 * when every triple fits it, so a row is only "Best match" if it suits every selected object.
	 * Undefined for a single object, which scores against its own dimensions as before.
	 */
	dimensionGroups?: TDimensionGroup[]
}

/** A row of the list, with everything needed to render and sort it. */
type TCandidate = {
	model: TCatalogueModelNumber
	match: ReturnType<typeof computeMatch>
	isMl: boolean
	relevance: number
}

const renderDimension = (label: string, value: number | null) => (
	<span>
		<span className={dimensionLabel}>{label}</span>
		{value === null ? <span className={unset}>Unset</span> : <span>{value}</span>}
	</span>
)

export const SelectModelNumberModal = ({
	isOpen,
	onClose,
	furniture,
	seriesOverrideId,
	initialSelectedId,
	onSave,
	isOther = false,
	isCustom = false,
	initialCustomModelNumberId = null,
	dimensionGroups,
}: Props) => {
	const { data: categoriesData } = useObjectsCategories()
	const data = categoriesData as unknown as TCategoriesWithModels | undefined
	const { data: seriesList } = useGetSeries()
	const seriesName = useMemo(
		() => seriesList?.find((s) => s.id === seriesOverrideId)?.series_name ?? null,
		[seriesList, seriesOverrideId],
	)

	// The catalogue-wide scope is meaningless for an "Other" object: its list already
	// spans every category, so there is no narrower scope to switch away from.
	const canSwitchScope = !isOther
	const [scope, setScope] = useState<TScope>(CATEGORY_SCOPE)
	// One search term PER SCOPE. The two tabs search different pools, so a shared
	// input made a catalogue query silently filter the object's own category too (and
	// vice versa) the moment the user switched tabs.
	const [categoryTerm, setCategoryTerm] = useState("")
	const [allTerm, setAllTerm] = useState("")
	const [customTerm, setCustomTerm] = useState("")
	/**
	 * ONE PICK PER TAB, never a shared one.
	 *
	 * Each scope draws from a different pool, so a pick only means anything inside the tab it was made
	 * in: the two Stevens pools disagree about which endpoint saves a row, and the custom pool is a
	 * different TABLE with its own id sequence (a Stevens model number and a custom definition can
	 * both be id 12). A single shared slot made a tab switch carry the previous tab's pick with it —
	 * Save stayed enabled and wrote a model number the user could no longer see.
	 *
	 * So: switching tabs never moves a pick, Save reads only the ACTIVE tab's slot (and is therefore
	 * inert on a tab nothing has been picked in), and switching back restores what that tab held —
	 * including the object's own current selection, which seeds its tab on open.
	 *
	 * The two Stevens slots hold the whole record rather than an id: the visible list is re-filtered
	 * by the search and re-fetched by the catalogue query, so a pick must survive its row leaving the
	 * screen. The custom slot holds an id because its record comes from a query that outlives any
	 * search (the whole drawing's definitions are in memory).
	 */
	const [categoryPick, setCategoryPick] = useState<TCatalogueModelNumber | null>(null)
	const [allPick, setAllPick] = useState<TCatalogueModelNumber | null>(null)
	const [customPickId, setCustomPickId] = useState<number | null>(initialCustomModelNumberId)
	const [isCreateOpen, setIsCreateOpen] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const anchorRef = useRef<HTMLDivElement | null>(null)
	const portalRef = useRef<HTMLDivElement | null>(null)
	const searchRef = useRef<InputRef | null>(null)

	const isAllScope = canSwitchScope && scope === ALL_SCOPE
	const isCustomScope = canSwitchScope && scope === CUSTOM_SCOPE
	// Whichever tab is active owns the input; the other tabs' terms are parked in state.
	const searchTerm = isAllScope ? allTerm : isCustomScope ? customTerm : categoryTerm
	const setSearchTerm = isAllScope ? setAllTerm : isCustomScope ? setCustomTerm : setCategoryTerm
	// Debounce the catalogue term only, so flipping tabs never briefly feeds the
	// category tab's text to the backend search.
	const debouncedAllTerm = useDebounce(allTerm, SEARCH_DEBOUNCE_MS)
	const trimmedAllTerm = allTerm.trim()
	const hasEnoughChars = trimmedAllTerm.length >= MIN_SEARCH_CHARS

	/**
	 * Custom Model Numbers belong to the DRAWING, not to the quotation — one definition is reusable
	 * across every quotation of the same drawing — so the list is keyed off `drawingID`, which the
	 * editor's own deep link always carries.
	 */
	const [searchParams] = useSearchParams()
	const drawingId = Number(searchParams.get("drawingID"))
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const customModelNumbersQuery = useGetCustomModelNumbers(drawingId)
	const { mutateAsync: createCustomModelNumberAsync, isPending: isCreatingCustom } = useCreateCustomModelNumber()

	const categoryId = furniture.category?.id
	const detectedModelNumbers = furniture.detected_model_numbers
	const { width: fWidth, height: fHeight, depth: fDepth } = furniture

	/** The object's own three-level category, for the amber dot and the footer notice. */
	const currentCategory = useMemo(
		() => ({
			topLevel: furniture.top_level_category?.name ?? null,
			superCategory: furniture.super_category?.name ?? null,
			category: furniture.category?.name ?? null,
		}),
		[furniture.top_level_category?.name, furniture.super_category?.name, furniture.category?.name],
	)

	useEffect(() => {
		if (!isOpen) return
		setAllPick(null)
		setCustomPickId(initialCustomModelNumberId)
		setCategoryTerm("")
		setAllTerm("")
		setCustomTerm("")
		setIsCreateOpen(false)
		// Open on the tab that can actually show something. A custom object (#1346) has no category at
		// all, so its own definitions lead; otherwise, with no category there is no category-scoped
		// list to browse — the normal state of the REQ-293 create panel before its dropdowns are
		// filled — and the catalogue leads. With a category, its own category leads.
		setScope(
			canSwitchScope && isCustom ? CUSTOM_SCOPE : canSwitchScope && categoryId == null ? ALL_SCOPE : CATEGORY_SCOPE,
		)
		setMetadataById(new Map())
		// The object's own selection seeds the OBJECT-CATEGORY tab specifically: the backend
		// guarantees the current model number sits in the object's own category, so that is the tab
		// it belongs to and the tab it must still be selected in after a round trip through the
		// others. Seeding a whole record (rather than an id) also makes Save live on the first
		// frame, before any list request resolves.
		const selected = furniture.selected_model_number
		setCategoryPick(
			initialSelectedId !== null && selected?.id === initialSelectedId
				? {
						id: selected.id,
						model_number: selected.model_number,
						image_from_stevens_catalogue: selected.image_from_stevens_catalogue,
						thumbnails_url: null,
						dimensions: {
							width: selected.dimensions.width,
							height: selected.dimensions.height,
							depth: selected.dimensions.depth,
							avg_size: String(selected.dimensions.avg_size ?? ""),
							min_size: String(selected.dimensions.min_size ?? ""),
						},
						categoryPath:
							currentCategory.category !== null
								? {
										topLevel: currentCategory.topLevel ?? "",
										superCategory: currentCategory.superCategory ?? "",
										category: currentCategory.category,
									}
								: null,
					}
				: null,
		)
	}, [
		isOpen,
		initialSelectedId,
		initialCustomModelNumberId,
		isCustom,
		furniture.selected_model_number,
		currentCategory,
		canSwitchScope,
		categoryId,
	])

	useEffect(() => {
		return () => {
			portalRef.current?.remove()
			portalRef.current = null
		}
	}, [])

	const getContainer = useCallback((): HTMLElement => {
		if (portalRef.current?.isConnected) return portalRef.current
		const root = anchorRef.current?.getRootNode()
		const host: ShadowRoot | HTMLElement =
			root instanceof ShadowRoot
				? root
				: (document.querySelector("apryse-webviewer") as HTMLElement | null)?.shadowRoot ?? document.body
		const container = document.createElement("div")
		container.setAttribute("data-portal", "select-model-number-modal")
		host.appendChild(container)
		portalRef.current = container
		return container
	}, [])

	const detectedSet = useMemo(
		() => new Set((detectedModelNumbers ?? []).map(normalizeModelNumber).filter(Boolean)),
		[detectedModelNumbers],
	)

	/**
	 * Object-category scope pool: the model numbers of the furniture's CURRENT category
	 * as the user sees it, which spans every `/categories/` node sharing that displayed
	 * path — see `findModelNumbersForCategory`. Built from `/categories/`, so these rows
	 * carry a path but no thumbnail and no prices; `metadataById` fills those in.
	 *
	 * ML-detected model numbers are NOT merged in from other categories any more. They
	 * used to be, so an ML pick found elsewhere still surfaced here — but once the user
	 * moves the object to another category that row is a cross-category model number
	 * rendered inside the object's own category tab: it no longer belongs to the
	 * category, and saving it through the plain furniture PATCH is rejected. An ML pick
	 * that IS in the current category comes in with the category list and still carries
	 * its "ML choice" tag (`detectedSet`, below). To reach one outside it, use the
	 * catalogue-wide scope, which moves the category as part of the save.
	 */
	const categoryModels = useMemo(
		() => (isOther ? findAllModelNumbers(data) : findModelNumbersForCategory(data, categoryId)),
		[data, categoryId, isOther],
	)
	const categoryBaseList = useMemo<TCatalogueModelNumber[]>(
		() => categoryModels.map(toCatalogueModelNumber),
		[categoryModels],
	)

	/**
	 * Which `/categories/` node each object-category row actually came from. A displayed
	 * category is split across several nodes (one per column / composite / shelf
	 * variant), so a row from this scope can still belong to a different category id
	 * than the furniture's — which decides the save endpoint below. Read off the model
	 * rather than the catalogue row because `TCatalogueModelNumber` keeps names, not ids.
	 */
	const categoryNodeIdByModelId = useMemo(() => {
		const map = new Map<number, number>()
		for (const model of categoryModels) {
			const nodeId = model.category_hierarchy["Model Category"]?.id
			if (nodeId) map.set(model.id, nodeId)
		}
		return map
	}, [categoryModels])

	// Catalogue-wide scope pool. Gated on the character minimum inside the hook, so
	// nothing is requested until the user has typed enough to narrow the catalogue.
	const searchQuery = useSearchModelNumbers(debouncedAllTerm, isOpen && isAllScope)
	const allScopeRows = useMemo(() => flattenCatalogue(searchQuery.data), [searchQuery.data])

	/**
	 * Filter + sort, using only what the active scope's rows already carry, so the
	 * result does not depend on the metadata request below (which is keyed off it).
	 *
	 * The object-category scope filters locally by model-number substring. The
	 * catalogue scope is NOT re-filtered: `name_contains=` matches a model number by
	 * its own name OR its category's name (typo-tolerant), so the response already is
	 * the match set. The sorts differ: the catalogue scope leads with relevance
	 * (someone who typed `10412` wants `10412` first — a category-only match ranks
	 * after every direct hit) and uses match quality as the tie-breaker, while the
	 * object-category scope keeps today's match-quality-only sort.
	 */
	const ranked = useMemo<TCandidate[]>(() => {
		const pool = isAllScope ? allScopeRows : categoryBaseList
		const term = (isAllScope ? debouncedAllTerm : categoryTerm).trim().toLowerCase()
		const filtered = !isAllScope && term ? pool.filter((m) => m.model_number.toLowerCase().includes(term)) : pool

		const enriched = filtered.map<TCandidate>((model) => ({
			model,
			match: dimensionGroups
				? computeMatchForMany(model.dimensions, dimensionGroups)
				: computeMatch(model.dimensions, { width: fWidth, height: fHeight, depth: fDepth }),
			isMl: detectedSet.has(normalizeModelNumber(model.model_number)),
			relevance: relevanceRank(model.model_number, term),
		}))

		return enriched.sort((a, b) => {
			if (isAllScope && a.relevance !== b.relevance) return a.relevance - b.relevance
			return matchQualityRank(a.match.tag, a.isMl) - matchQualityRank(b.match.tag, b.isMl)
		})
	}, [
		isAllScope,
		allScopeRows,
		categoryBaseList,
		debouncedAllTerm,
		categoryTerm,
		detectedSet,
		fWidth,
		fHeight,
		fDepth,
		dimensionGroups,
	])

	/**
	 * The selection is NOT lifted out of `ranked`: a row that matches the search stays
	 * exactly where the sort put it and only its radio turns on. Moving it to a "Selected"
	 * block at the top re-ordered the list under the user mid-scan, which made it look
	 * like a different result set. The selection surviving a search it does not match is
	 * carried by the tab's own stored pick (state), not by a row on screen.
	 */
	const visible = useMemo(() => ranked.slice(0, MAX_VISIBLE_RESULTS), [ranked])

	/**
	 * One request for the thumbnails and prices of exactly the rows on screen —
	 * replacing the two-requests-per-row (`{id}/` + `catalogue_image/`) the modal used
	 * to fire. Catalogue-scope rows arrive complete, so only the object-category pool
	 * needs this; the picked id is always included so that row can show its
	 * own thumbnail even when it lives in a category the list never loaded.
	 *
	 * The id list is DEBOUNCED even though the object-category filter itself is instant:
	 * the visible rows change on every keystroke, and keying the request off them
	 * directly fired one request per character.
	 */
	const visibleIdsKey = useMemo(
		() => (isAllScope ? "" : visible.map((c) => c.model.id).join(",")),
		[isAllScope, visible],
	)
	const debouncedIdsKey = useDebounce(visibleIdsKey, SEARCH_DEBOUNCE_MS)
	const metadataIds = useMemo(() => {
		const ids = debouncedIdsKey ? debouncedIdsKey.split(",").map(Number) : []
		// The object-category pick is always included so its row can show its own thumbnail even when
		// it lives outside the rows currently on screen.
		if (categoryPick !== null) ids.push(categoryPick.id)
		return Array.from(new Set(ids))
	}, [debouncedIdsKey, categoryPick])
	const metadataQuery = useGetModelNumbersByIds(metadataIds, isOpen)

	/**
	 * Metadata ACCUMULATES across requests for the lifetime of the open modal. Deriving
	 * it from the latest response alone meant every new request blanked the rows it did
	 * not cover — and because an absent `prices` field reads as "not requested" rather
	 * than "no price", the "No price" warning vanished and came back while the user
	 * typed. Rows keep whatever they already knew.
	 *
	 * `isOpen` is a dependency on purpose (#1463): the open-reset above empties the map, and
	 * a reopen with the same visible ids is a cache hit that hands back the SAME `data`
	 * reference, so keyed on `data` alone this effect never re-ran and "No price" stayed
	 * gone for the rest of the session. Declared after the reset, so its queued update
	 * lands on the emptied map.
	 */
	const [metadataById, setMetadataById] = useState<Map<number, TCatalogueModelNumber>>(new Map())
	useEffect(() => {
		if (!isOpen) return
		const rows = flattenCatalogue(metadataQuery.data)
		if (rows.length === 0) return
		setMetadataById((prev) => {
			const next = new Map(prev)
			for (const model of rows) next.set(model.id, model)
			return next
		})
	}, [metadataQuery.data, isOpen])

	/**
	 * Every record we currently know about, richest source last. Feeds the pinned row
	 * and each visible row's thumbnail / price lookup.
	 */
	const knownById = useMemo(() => {
		const map = new Map<number, TCatalogueModelNumber>()
		for (const model of categoryBaseList) map.set(model.id, model)
		for (const model of allScopeRows) map.set(model.id, model)
		for (const [id, model] of metadataById) map.set(id, model)
		return map
	}, [categoryBaseList, allScopeRows, metadataById])

	/**
	 * The Custom tab's rows: the drawing's definitions, filtered by the tab's own term and sorted by
	 * dimension match alone.
	 *
	 * Only the match badge applies here, per the issue: a custom definition carries its own price, so
	 * "No price" can never be true of one, and nothing detects them, so "ML choice" cannot either.
	 * Both are switched off at the render site rather than computed and discarded.
	 */
	const customCandidates = useMemo(() => {
		const rows = customModelNumbersQuery.data ?? []
		const filtered = rows.filter((custom) => customModelNumberMatchesTerm(custom, customTerm))
		return filtered
			.map((custom) => {
				const model = toSelectableCustomModelNumber(custom)
				return {
					custom,
					model,
					match: dimensionGroups
						? computeMatchForMany(model.dimensions, dimensionGroups)
						: computeMatch(model.dimensions, { width: fWidth, height: fHeight, depth: fDepth }),
				}
			})
			.sort((a, b) => matchQualityRank(a.match.tag, false) - matchQualityRank(b.match.tag, false))
	}, [customModelNumbersQuery.data, customTerm, dimensionGroups, fWidth, fHeight, fDepth])

	const visibleCustom = useMemo(() => customCandidates.slice(0, MAX_VISIBLE_RESULTS), [customCandidates])

	const selectedCustomModel = useMemo(
		() => (customModelNumbersQuery.data ?? []).find((custom) => custom.id === customPickId) ?? null,
		[customModelNumbersQuery.data, customPickId],
	)

	/** Picking in the Custom tab touches only the Custom tab — see the per-scope pick state above. */
	const handleSelectCustom = useCallback((id: number) => {
		setCustomPickId(id)
	}, [])

	/**
	 * Creating a definition from inside the picker. The new one is selected straight away — the user
	 * opened this dialog to pick something, and making them find their own new row in the list is a
	 * step with no decision in it. The list is invalidated on the SHARED query key, so the
	 * management screen (#1345) shows it too.
	 */
	const handleCreateCustomModelNumber = useCallback(
		async (payload: TCustomModelNumberPayload) => {
			try {
				const response = await createCustomModelNumberAsync({ drawingId, payload })
				await queryClient.invalidateQueries({ queryKey: [CUSTOM_MODEL_NUMBERS_QUERY_KEY, drawingId] })
				const created = response?.data
				if (created?.id != null) handleSelectCustom(created.id)
				setIsCreateOpen(false)
			} catch (e) {
				// Surfaced as a toast; the dialog stays open so the typed values are not lost.
				formatError(e)
			}
		},
		[createCustomModelNumberAsync, drawingId, queryClient, handleSelectCustom, formatError],
	)

	const handleScopeChange = useCallback((next: string | number) => {
		setScope(next === ALL_SCOPE ? ALL_SCOPE : next === CUSTOM_SCOPE ? CUSTOM_SCOPE : CATEGORY_SCOPE)
		// The catalogue scope has no browsable default, so typing is the only way
		// forward — put the caret where the user has to act. The Custom scope DOES browse
		// (the drawing's definitions are few), so it is left alone.
		if (next === ALL_SCOPE) requestAnimationFrame(() => searchRef.current?.focus())
	}, [])

	/**
	 * A Stevens pick lands in the slot of the tab it was made in, and nowhere else. Which tab that is
	 * decides the save endpoint later, so recording it here — rather than reading the live tab at save
	 * time — is what stops a catalogue pick from being sent through the plain PATCH that rejects it.
	 */
	const handleSelect = useCallback(
		(model: TCatalogueModelNumber) => {
			// The row hands over its OWN record rather than an id to look up: a lookup that missed would
			// make the click a silent no-op, which is a far worse failure than a slightly thinner record.
			// `knownById` is still preferred when it has something richer (thumbnail, prices).
			const record = knownById.get(model.id) ?? model
			if (isAllScope) setAllPick(record)
			else setCategoryPick(record)
		},
		[isAllScope, knownById],
	)

	/** The pick Save will act on: the ACTIVE tab's, and only that one. */
	const activeStevensPick = isAllScope ? allPick : isCustomScope ? null : categoryPick

	const getRowProps = useCallback(
		(candidate: TCandidate) => {
			const known = knownById.get(candidate.model.id) ?? candidate.model
			return {
				model: candidate.model,
				isSelected: activeStevensPick?.id === candidate.model.id,
				isMlChoice: candidate.isMl,
				match: candidate.match,
				hasPrice: hasPriceForSeries(known, seriesOverrideId),
				thumbnailUrl: known.thumbnails_url,
				// `ModelNumberRow` calls this with the row's id; the candidate is closed over instead, so
				// the record always reaches `handleSelect`.
				onSelect: () => handleSelect(candidate.model),
				showCategoryPath: isAllScope,
				categoryPath: candidate.model.categoryPath,
				isCategoryChange: isAllScope && isCategoryChangeFor(candidate.model.categoryPath, currentCategory),
			}
		},
		[knownById, activeStevensPick, handleSelect, seriesOverrideId, isAllScope, currentCategory],
	)

	const hasUnsetDim = dimensionGroups
		? dimensionGroups.some((g) => g.width === null || g.height === null || g.depth === null)
		: furniture.width === null || furniture.height === null || furniture.depth === null

	// Save works off the ACTIVE tab's stored record, never off the visible list — so narrowing the
	// search cannot silently disable it, and switching to a tab nothing has been picked in leaves it
	// inert rather than saving the previous tab's row.
	const canSave = isCustomScope ? selectedCustomModel !== null : activeStevensPick !== null

	/**
	 * True when the pick sits in the object's displayed category but on a DIFFERENT
	 * `/categories/` node than the furniture — a peer variant (column count, composite
	 * index, shelf count). Saving it moves the furniture's `related_category`, so it
	 * needs the arbitrary endpoint even though nothing changes on screen. Both ids must
	 * be known before this can fire: an "Other" object has no category of its own, and
	 * flagging it here would reroute a save that works fine today.
	 */
	const isCrossNodePick = useMemo(() => {
		if (categoryPick === null || categoryId == null) return false
		const nodeId = categoryNodeIdByModelId.get(categoryPick.id)
		return nodeId != null && nodeId !== categoryId
	}, [categoryPick, categoryId, categoryNodeIdByModelId])

	const handleSave = async () => {
		if (isSaving) return

		/**
		 * Dispatch on the ACTIVE tab, never on "is a custom pick set anywhere".
		 *
		 * A custom object seeds the Custom tab's slot the moment the picker opens, so a tab-blind check
		 * of that slot sent the custom-model-number request even while the user was on the catalogue tab
		 * looking at a Stevens pick — which is the exact confusion per-tab picks exist to prevent. The
		 * chosen tab decides both WHAT is saved and WHICH endpoint saves it.
		 */
		const pick = isCustomScope
			? selectedCustomModel && {
					model: toSelectableCustomModelNumber(selectedCustomModel),
					options: {
						customModelNumber: selectedCustomModel,
						useArbitraryEndpoint: false,
						categoryNodeId: null,
					},
				}
			: activeStevensPick && {
					model: activeStevensPick,
					options: {
						customModelNumber: null,
						// A catalogue-tab pick may belong to another category, and a category-tab pick may sit
						// on a peer node of the same displayed category — both move `related_category`, which
						// the plain furniture PATCH rejects.
						useArbitraryEndpoint: isAllScope || isCrossNodePick,
						categoryNodeId: categoryNodeIdByModelId.get(activeStevensPick.id) ?? null,
					},
				}
		if (!pick) return

		setIsSaving(true)
		try {
			await onSave(pick.model, pick.options)
			onClose()
		} catch {
			// Error already surfaced by the parent (e.g. via toast). The modal deliberately stays OPEN so
			// the user can pick something else — a model number with no category is rejected by the
			// backend, and that rejection has to be recoverable in place.
		} finally {
			setIsSaving(false)
		}
	}

	/**
	 * Footer notice — one slot, three states, no new element. Only the catalogue scope
	 * can produce a category move, so the object-category scope always lands on
	 * "nothing to announce".
	 */
	const notice = useMemo(() => {
		// Scoped to the catalogue tab's OWN pick. Save acts only on the active tab, so leaving that tab
		// makes the category move it warns about unreachable — and a warning that outlived the tab it
		// belonged to described a save the button could no longer perform.
		if (!isAllScope || !allPick) return null
		const pinnedModel = allPick
		if (pinnedModel.categoryPath === null) {
			return {
				isStop: true,
				content: (
					<span>
						<b className={footerNoticeStrong}>{pinnedModel.model_number}</b> has no category. An admin needs to
						assign one before it can be used.
					</span>
				),
			}
		}
		if (!isCategoryChangeFor(pinnedModel.categoryPath, currentCategory)) return null
		return {
			isStop: false,
			content: (
				<span>
					Saving <b className={footerNoticeStrong}>{pinnedModel.model_number}</b> changes this object&apos;s category
					to <b className={footerNoticeStrong}>{splitCategoryPath(pinnedModel.categoryPath).leaf}</b>.
				</span>
			),
		}
	}, [isAllScope, allPick, currentCategory])

	/** List area of the catalogue scope: teach the rule, then report the outcome. */
	const renderAllScopeState = () => {
		if (!hasEnoughChars) {
			return (
				<div className={stateBlock}>
					<span className={stateTitle}>Keep typing to search the full catalogue</span>
					<span className={stateSubtitle}>Enter at least {MIN_SEARCH_CHARS} characters of a model number.</span>
				</div>
			)
		}
		if (searchQuery.isFetching || debouncedAllTerm.trim() !== trimmedAllTerm) {
			return (
				<div className={stateBlock}>
					<span className={stateTitle}>Searching the full catalogue…</span>
				</div>
			)
		}
		// A failed request is not an empty result set — saying "nothing matched" would
		// send the user off to check a model number that may well exist.
		if (searchQuery.isError) {
			return (
				<div className={stateBlock}>
					<span className={stateTitle}>Couldn&apos;t search the catalogue</span>
					<span className={stateSubtitle}>The search request failed. Check your connection and try again.</span>
				</div>
			)
		}
		return (
			<div className={stateBlock}>
				<span className={stateTitle}>No model numbers match “{trimmedAllTerm}”</span>
				<span className={stateSubtitle}>Check the number, or switch back to the object&apos;s category to browse.</span>
			</div>
		)
	}

	/**
	 * List area of the object-category scope when it has nothing to show. The two reasons
	 * are different problems for the user and must not share one line: the category itself
	 * carries no model numbers (120 of the 222 displayed categories are empty across ALL
	 * their nodes — grouping rescues none of them — and moving an object into one is
	 * exactly how an estimator lands here), or the search narrowed a non-empty list to
	 * nothing. Both point at the catalogue scope, the only way forward from here.
	 */
	const renderCategoryScopeState = () => {
		if (categoryBaseList.length === 0) {
			return (
				<div className={stateBlock}>
					<span className={stateTitle}>
						No model numbers are assigned to {currentCategory.category ?? "this object's category"}
					</span>
					<span className={stateSubtitle}>
						{canSwitchScope
							? "Search the whole catalogue in All model numbers — picking one there moves the object to its category."
							: "Contact an admin to assign model numbers to this category."}
					</span>
				</div>
			)
		}
		return (
			<div className={stateBlock}>
				<span className={stateTitle}>No model numbers match “{categoryTerm.trim()}”</span>
				<span className={stateSubtitle}>
					{canSwitchScope
						? "Clear the search, or look in All model numbers for the rest of the catalogue."
						: "Clear the search to see the whole list again."}
				</span>
			</div>
		)
	}

	/**
	 * List area of the Custom scope. "The drawing has none yet" and "your search matched none of
	 * them" are different problems — the first is answered by the Create button above the list, the
	 * second by clearing the search — so they must not share one line.
	 */
	const renderCustomScopeState = () => {
		if (customModelNumbersQuery.isLoading) {
			return (
				<div className={stateBlock}>
					<span className={stateTitle}>Loading custom model numbers…</span>
				</div>
			)
		}
		// A failed request is not an empty drawing — inviting the user to create a duplicate of a
		// definition that already exists is worse than saying the list could not be loaded.
		if (customModelNumbersQuery.isError) {
			return (
				<div className={stateBlock}>
					<span className={stateTitle}>Couldn&apos;t load custom model numbers</span>
					<span className={stateSubtitle}>The request failed. Check your connection and try again.</span>
				</div>
			)
		}
		if ((customModelNumbersQuery.data ?? []).length === 0) {
			return (
				<div className={stateBlock}>
					<span className={stateTitle}>No custom model numbers in this drawing yet</span>
					<span className={stateSubtitle}>
						Create one to price an object that isn&apos;t in the Stevens catalogue. It can be reused across this
						drawing&apos;s quotations.
					</span>
				</div>
			)
		}
		return (
			<div className={stateBlock}>
				<span className={stateTitle}>No custom model numbers match “{customTerm.trim()}”</span>
				<span className={stateSubtitle}>Clear the search to see the whole list, or create a new definition.</span>
			</div>
		)
	}

	const isTruncated = ranked.length > MAX_VISIBLE_RESULTS
	const resultsLabel = isTruncated
		? `Results · showing first ${MAX_VISIBLE_RESULTS} of ${ranked.length} — refine your search`
		: `Results · ${ranked.length}`

	return (
		<>
			<div ref={anchorRef} style={{ display: "none" }} aria-hidden />
			<Modal
				isModalOpen={isOpen}
				setModalClose={onClose}
				width={640}
				title="Select model number"
				getContainer={getContainer}
			>
				<div className={body}>
					<div className={section}>
						<span className={sectionTitle}>Cabinet dimensions</span>
						{dimensionGroups ? (
							/* Batch mode: one line per DISTINCT triple, two columns, with a `(n)` suffix
							   wherever a triple is shared by more than one selected object. */
							<div className={dimensionGroupGrid}>
								{dimensionGroups.map((group) => (
									<div className={dimensionsRow} key={`${group.width}-${group.height}-${group.depth}`}>
										{renderDimension("W: ", group.width)}
										{renderDimension("H: ", group.height)}
										{renderDimension("D: ", group.depth)}
										{group.count > 1 ? <span className={dimensionCount}>({group.count})</span> : null}
									</div>
								))}
							</div>
						) : (
							<div className={dimensionsRow}>
								{renderDimension("W: ", furniture.width)}
								{renderDimension("H: ", furniture.height)}
								{renderDimension("D: ", furniture.depth)}
							</div>
						)}
						{hasUnsetDim ? (
							<div className={warning}>Some dimensions are missing — model number matching may be inaccurate.</div>
						) : null}
					</div>

					<div className={section}>
						<span className={sectionTitle}>Series</span>
						{seriesName ? (
							<span className={seriesValue}>{seriesName}</span>
						) : (
							<div className={warning}>No series selected — model number price availability cannot be determined.</div>
						)}
					</div>

					{canSwitchScope ? (
						<div className={scopeRow}>
							<SegmentedWithoutBg
								className={segmented}
								value={scope}
								onChange={handleScopeChange}
								options={[
									{ label: `Object category · ${categoryBaseList.length}`, value: CATEGORY_SCOPE },
									{ label: "All model numbers", value: ALL_SCOPE },
									{ label: `Custom · ${(customModelNumbersQuery.data ?? []).length}`, value: CUSTOM_SCOPE },
								]}
							/>
						</div>
					) : null}

					<div className={search}>
						<Input
							ref={searchRef}
							placeholder="Search by model number"
							prefix={<SearchGlassIcon className={searchIcon} />}
							size="large"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
					</div>

					{isCustomScope ? (
						<>
							<div className={list}>
								{visibleCustom.length === 0 ? (
									renderCustomScopeState()
								) : (
									<>
										{customCandidates.length > MAX_VISIBLE_RESULTS ? (
											<span className={groupLabel}>
												{`Results · showing first ${MAX_VISIBLE_RESULTS} of ${customCandidates.length} — refine your search`}
											</span>
										) : null}
										{visibleCustom.map((candidate) => (
											<ModelNumberRow
												key={candidate.custom.id}
												model={candidate.model}
												isSelected={customPickId === candidate.custom.id}
												/* Neither badge can apply to a custom definition: it carries its own price, and
												   nothing detects one. */
												isMlChoice={false}
												hasPrice
												match={candidate.match}
												thumbnailUrl={null}
												onSelect={handleSelectCustom}
											/>
										))}
									</>
								)}
							</div>
						</>
					) : isAllScope ? (
						<div className={list}>
							{visible.length === 0 ? (
								renderAllScopeState()
							) : (
								<>
									<span className={groupLabel}>{resultsLabel}</span>
									{visible.map((candidate) => (
										<ModelNumberRow key={candidate.model.id} {...getRowProps(candidate)} />
									))}
								</>
							)}
						</div>
					) : (
						<div className={list}>
							{visible.length === 0 ? (
								renderCategoryScopeState()
							) : (
								<>
									{/* The cap applies to BOTH scopes, so it has to be reported in both. Shown
									    only when it actually bites, so the default tab gains no new chrome
									    until there is something to say — an "Other" object's list spans every
									    category and truncating it silently hides most of the catalogue. */}
									{isTruncated ? <span className={groupLabel}>{resultsLabel}</span> : null}
									{visible.map((candidate) => (
										<ModelNumberRow key={candidate.model.id} {...getRowProps(candidate)} />
									))}
								</>
							)}
						</div>
					)}

					<div className={footer}>
						{notice ? (
							<span className={`${footerNotice} ${notice.isStop ? footerNoticeStop : ""}`}>
								<span className={footerNoticeDot}>!</span>
								{notice.content}
							</span>
						) : null}
						{/* Per design: its own button on the LEFT of the footer, opposite Cancel/Save — not a link
						    above the list. Only on the Custom tab, which is the only pool it can add to. */}
						{isCustomScope ? (
							<ButtonSecondary
								type="button"
								className={`${footerButton} ${createButton}`}
								onClick={() => setIsCreateOpen(true)}
							>
								Create custom model number
							</ButtonSecondary>
						) : null}
						<div className={footerButtons}>
							<ButtonSecondary type="button" className={footerButton} onClick={onClose}>
								Cancel
							</ButtonSecondary>
							<ButtonPrimary className={footerButton} submittable={canSave && !isSaving} onClick={handleSave}>
								Save
							</ButtonPrimary>
						</div>
					</div>
				</div>
			</Modal>
			{/* Reuses #1345's dialog rather than a second create form. `getContainer` is what makes it
			    usable here at all: the editor lives in the Apryse shadow root, and antd would otherwise
			    portal it to `document.body`, outside every injected stylesheet. */}
			<CustomModelNumberModal
				customModelNumber={null}
				isOpen={isCreateOpen}
				isPending={isCreatingCustom}
				onClose={() => setIsCreateOpen(false)}
				onSave={handleCreateCustomModelNumber}
				getContainer={getContainer}
			/>
		</>
	)
}
